import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LeadsBoardPage from './pages/LeadsBoardPage';
import AllLeadsPage from './pages/AllLeadsPage';
import TasksPage from './pages/TasksPage';
import ActivityFeedPage from './pages/ActivityFeedPage';
import CallLogsPage from './pages/CallLogsPage';
import ReportsPage from './pages/ReportsPage';
import DailyReportsPage from './pages/DailyReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import R2GDashboardPage from './pages/R2GDashboardPage';
import RemindersPage from './pages/RemindersPage';
import MeetingRoomPage from './pages/MeetingRoomPage';
import QuickCallPage from './pages/QuickCallPage';
import LMSVirtualMeetPage from './pages/LMSVirtualMeetPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ExecutiveReportPage from './pages/ExecutiveReportPage';
import TeamsPage from './pages/TeamsPage';
import LoiAnalysisPage from './pages/LoiAnalysisPage';
import ReportsAnalyticsPage from './pages/ReportsAnalyticsPage';
import ImportPage from './pages/ImportPage';
import SheetMonitorPage from './pages/SheetMonitorPage';
import CallActivityPage from './pages/CallActivityPage';
import JitsiRoomPage from './pages/JitsiRoomPage';
import GuestMeetPage from './pages/GuestMeetPage';
import FranchiseOutletsPage from './pages/FranchiseOutletsPage';

// Components
// AIChatbot removed (Jul 2025) — floating chat widget deprecated per product decision.
import FloatingTools from './components/widgets/FloatingTools';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';
import MobileBottomNav from './components/layout/MobileBottomNav';

// Hidden Reports Route Guard — strict allow-list: Super Admin, CEO, Manager only
const HiddenReportsRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const ALLOWED = ['Super Admin', 'CEO', 'Manager'];
  if (!user || !ALLOWED.includes(user.role)) {
    // Silently bounce – do not reveal route exists
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireManager = false, allowR2G = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // R2G users can only access R2G dashboard
  if (user.role === 'R2G' && !allowR2G) {
    return <Navigate to="/r2g" replace />;
  }

  if (requireManager && (user.role === 'Executive' || user.role === 'R2G')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // R2G users go to R2G dashboard
    if (user.role === 'R2G') {
      return <Navigate to="/r2g" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* Public Meeting Room Route (no auth required for guests) */}
      <Route path="/meet/:roomId" element={<MeetingRoomPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute allowR2G>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="leads-board" element={<LeadsBoardPage />} />
        <Route
          path="all-leads"
          element={
            <ProtectedRoute requireManager>
              <AllLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="reminders" element={<RemindersPage />} />
        <Route path="quick-call" element={<QuickCallPage />} />
        <Route path="call-activity" element={<CallActivityPage />} />
        <Route path="lms-meet" element={<LMSVirtualMeetPage />} />
        <Route path="lms-meet/room/:meetingId" element={<JitsiRoomPage />} />
        <Route path="franchise-outlets" element={<FranchiseOutletsPage />} />
        <Route
          path="activity-feed"
          element={
            <ProtectedRoute requireManager>
              <ActivityFeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="call-logs"
          element={<CallLogsPage />}
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute requireManager>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="executive-report"
          element={
            <ProtectedRoute requireManager>
              <ExecutiveReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams"
          element={
            <ProtectedRoute requireManager>
              <TeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="loi-analysis"
          element={
            <ProtectedRoute requireManager>
              <LoiAnalysisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute requireManager>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="daily-reports"
          element={
            <ProtectedRoute requireManager>
              <DailyReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute requireManager>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
        {/* HIDDEN: Admin Reports & Analytics — Super Admin / CEO / Manager only, NO sidebar link */}
        <Route
          path="admin/reports-analytics"
          element={
            <HiddenReportsRoute>
              <ReportsAnalyticsPage />
            </HiddenReportsRoute>
          }
        />
        {/* Lead Database Import — Super Admin / CEO / Manager */}
        <Route
          path="import"
          element={
            <HiddenReportsRoute>
              <ImportPage />
            </HiddenReportsRoute>
          }
        />
        {/* Sheet Monitor — Super Admin / CEO / Manager */}
        <Route
          path="sheet-monitor"
          element={
            <HiddenReportsRoute>
              <SheetMonitorPage />
            </HiddenReportsRoute>
          }
        />
        <Route 
          path="r2g" 
          element={
            <ProtectedRoute allowR2G>
              <R2GDashboardPage />
            </ProtectedRoute>
          } 
        />
      </Route>

      {/* Guest Meeting - no login required */}
      <Route path="/guest/meeting/:meetingId" element={<GuestMeetPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <MobileBottomNav />
        {!window.location.pathname.startsWith("/guest/") && <FloatingTools />}
        <Toaster
          position="top-right"
          richColors
          closeButton
          expand={false}
          visibleToasts={4}
          toastOptions={{
            className: 'rounded-xl shadow-lg',
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
